/**
 * ProgressSubMessage
 *
 * Shared component for displaying a progress message with an optional
 * token-count pill. Used by both GenerationProgress and PlanClarificationDialog.
 */

import React from 'react';
import {Box, Typography, Chip} from '@mui/material';

export interface ProgressSubMessageProps {
  /** Progress message text (without token info) */
  message: string;
  /** Optional token count — displayed as a small pill */
  tokens?: number | null;
  /** Approximate token count (shows ~ prefix). Default false. */
  approximate?: boolean;
}

function formatTokens(count: number, approximate?: boolean): string {
  const prefix = approximate ? '~' : '';
  if (count >= 1000) return `${prefix}${(count / 1000).toFixed(1)}k tokens`;
  return `${prefix}${count} tokens`;
}

const ProgressSubMessage: React.FC<ProgressSubMessageProps> = ({message, tokens, approximate}) => {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          color: '#6B7280',
          fontStyle: 'italic',
          whiteSpace: 'nowrap',
        }}
      >
        {message}
      </Typography>
      {tokens != null && tokens > 0 && (
        <Chip
          label={formatTokens(tokens, approximate)}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 500,
            bgcolor: '#F3F4F6',
            color: '#6B7280',
            '& .MuiChip-label': {px: 0.75, py: 0},
          }}
        />
      )}
    </Box>
  );
};

export default ProgressSubMessage;
